import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import MultiChipDown, { DropdownItem } from "pro-features/components/MultiChipDown/src/MultiChipDown";
import { Operator } from "pro-features/components/MultiChipDown/src/MultiChipDownTypes";
import { translate } from "app/filters/i18nFilter";
import {
    getOptions,
    getInitialSelectedItems,
    items,
} from "app/pages/website-analysis/demographicsUserBased/trafficAndEngagmentTable/tableTop/Filters/getAgeFilterItems";
import type { IGenderID } from "app/pages/website-analysis/demographicsUserBased/trafficAndEngagmentTable/tableTop/Filters/getGenderFilterItems";
import { DEFAULT_WEBSITES_DELIMITER } from "app/services/sitesService/consts";
import { allTrackers } from "app/services/track/track";
import { SELECT_ALL_AGES } from "app/UtilitiesAndConstants/Constants/age";
import type { IAgeBucketIDs } from "app/UtilitiesAndConstants/types/age";
import { resolveSelectedTextHOF } from "app/UtilitiesAndConstants/UtilityFunctions/ageFilter";

interface IAgeFilter {
    ageBucketIDs: IAgeBucketIDs;
    setAgeBucketIDs: (ageBucketIDs: IAgeBucketIDs) => void;
    genderID: IGenderID;
}

const CONTAINER_CLASS_NAME = "age-filter-multi-select";
const POPUP_CLASS_NAME = "DropdownContent-container";
const DROP_DOWN_POPUP_HEIGHT = 340;
const OPEN_DOWN_SITES_AMOUNT_THRESHOLD = 4;

const MultiSelectContainer = styled.div<{ openUpwards?: boolean }>`
    position: relative;
    ${({ openUpwards }) =>
        openUpwards &&
        `
        .${POPUP_CLASS_NAME} {
        /* the important property in needed since the popup has value */
        top: -${DROP_DOWN_POPUP_HEIGHT}px !important;
    }
    `}
`;

export const AgeFilter: FC<IAgeFilter> = (props) => {
    const { ageBucketIDs, setAgeBucketIDs, genderID } = props;
    const { params } = useCurrentStateAndParams();
    const { key } = params;
    const selectedSites = key.split(DEFAULT_WEBSITES_DELIMITER);
    const openUpwards = selectedSites.length < OPEN_DOWN_SITES_AMOUNT_THRESHOLD;
    const onApply = (selectedAgeBuckets) => {
        const ageBucketIDs = selectedAgeBuckets.map((ageBucket) => ageBucket.id);
        setAgeBucketIDs(ageBucketIDs);
    };
    const trackingOnApply = (selectedAgeBuckets) => {
        const trackingAges = selectedAgeBuckets
            .filter(({ id }) => id !== SELECT_ALL_AGES)
            .map(({ text }) => text)
            .join();
        const eventName = genderID ? `Age/${trackingAges}/${genderID}` : `Age/${trackingAges}`;
        allTrackers.trackEvent("Drop down", "apply", eventName);
    };

    const onItemClick = (selectedItem, selectedItems) => {
        const { id } = selectedItem;
        const isChecked = selectedItems.some((item) => item.id === selectedItem.id);
        const displayText = items.find((item) => item.id === id).textKey;
        allTrackers.trackEvent("Drop down", isChecked ? "unclick" : "click", `Age/${displayText}`);
    };
    const resolveSelectedText = resolveSelectedTextHOF(items);
    const isAllGenders = genderID === "all";
    const shouldDisableApplyButton = (selectedItems) => {
        const ageBucketIDs = selectedItems.map(({ id }) => id);
        return isAllGenders && items.slice(1).every((item) => ageBucketIDs.includes(item.id));
    };
    const onToggle = (isOpen) => {
        if (isOpen) {
            allTrackers.trackEvent("Drop down", "open", "Age");
        }
    };

    return (
        <MultiSelectContainer
            className={CONTAINER_CLASS_NAME}
            openUpwards={openUpwards}
        >
            <MultiChipDown
                data-automation="age-filter-multi-chip-down"
                clearSelectionWhenOneOption={false}
                dropdownWidth={200}
                dropdownPopupHeight={DROP_DOWN_POPUP_HEIGHT}
                options={getOptions(isAllGenders)}
                initialSelectedItems={getInitialSelectedItems(ageBucketIDs)}
                buttonText={translate("demographics.user.based.compare.table.filters.age.button")}
                onDone={onApply}
                trackingOnApply={trackingOnApply}
                titleOperator={Operator.Or}
                selectedText={resolveSelectedText(ageBucketIDs)}
                shouldDisableApplyButton={shouldDisableApplyButton}
                onToggleDropdown={onToggle}
                onItemClick={onItemClick}
                getDropdownItem={DropdownItem}
                appendTo={`.${CONTAINER_CLASS_NAME}`}
                cssClassContainer={POPUP_CLASS_NAME}
            />
        </MultiSelectContainer>
    );
};
